import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../api/client'
import { useLocale } from '../i18n/context'
import {
  useFillAllPreflight, useFillBatchStatus, useFillSelectionPreflight, useStartFillAll,
  useStopFillBatch,
  type FillAllPreflightResponse, type FillBatchStatusResponse,
} from './useFillAll'
import { useFillCard } from './useFill'
import { useResolveSelection } from './useCards'
import { EMPTY_SELECTION, toSelectionRequest, type DeckSelection } from '../utils/deckSelection'

/**
 * The two Fill flows a deck screen offers, as flows rather than as endpoints.
 *
 * Both used to live inside `DeckMobileFeed`, which is why the desktop deck
 * screen had no `Fill empty cards` at all: the preflight/confirm/poll sequence,
 * the selection-resolution refusal and the cost disclosure's wiring were all
 * phone-component internals, and reaching them from the desktop menu would have
 * meant writing them a second time. A second implementation of Fill's
 * disclosure is two places for the same promise to drift — so the sequence
 * moved here and both surfaces call it. `FillAllSheet` (the disclosure itself)
 * and `FillAllProgressBar` were already shared; this is the part that was not.
 *
 * Nothing about the semantics changed in the move: `allow_ai=False` on both
 * preflights, so opening or cancelling a sheet spends no AI quota; a selection
 * that cannot be resolved server-side is refused rather than run over the pages
 * a client happens to hold (ADR-067 Decision 6); and a single card's Fill
 * failing does not abort the rest.
 */

export interface FillAllFlow {
  /** The split, once it has arrived. */
  preflight: FillAllPreflightResponse | null
  preflightError: string | null
  /** The preflight request is in flight. */
  loading: boolean
  /** The batch's own start request is in flight. */
  starting: boolean
  confirm: () => void
  /** The running batch, or null when none is. */
  batchStatus: FillBatchStatusResponse | null
  stopping: boolean
  stop: () => void
}

/**
 * Deck-wide `Fill empty cards`: preflight on open, start on confirm, then poll
 * the batch while it runs, refreshing the card list as cards fill in place.
 *
 * `open` is the caller's — the menu item that summons the sheet lives above
 * whichever component renders it — and `onClose` is called once the batch has
 * genuinely started, never on the confirm click alone.
 */
export function useFillAllFlow(deckId: string, open: boolean, onClose: () => void): FillAllFlow {
  const { tr } = useLocale()
  const qc = useQueryClient()

  const [preflight, setPreflight] = useState<FillAllPreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [fillBatchId, setFillBatchId] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)

  const preflightMutation = useFillAllPreflight()
  const startFillAll = useStartFillAll()
  const stopFillAll = useStopFillBatch()
  const batchStatus = useFillBatchStatus(fillBatchId, fillBatchId !== null)

  // Run a fresh preflight every time the sheet is (re)opened — corpus state
  // may have moved on since the last time it was shown.
  useEffect(() => {
    if (!open) return
    setPreflight(null)
    setPreflightError(null)
    preflightMutation.mutate(deckId, {
      onSuccess: (data) => setPreflight(data),
      onError: (err) => setPreflightError(extractErrorMessage(err, tr.deckManagement.fillAllErrorGeneric)),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deckId])

  // Cards refresh as the batch fills them — "bubbles fill in place" — by
  // invalidating the card list whenever the poll observes new progress.
  const lastFilledRef = useRef(0)
  useEffect(() => {
    const data = batchStatus.data
    if (!data) return
    const filled = data.dictionary_filled + data.ai_filled + data.ai_failed
    if (filled !== lastFilledRef.current) {
      lastFilledRef.current = filled
      qc.invalidateQueries({ queryKey: ['cards', deckId] })
    }
    if (data.status !== 'running') setStopping(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchStatus.data])

  function confirm() {
    startFillAll.mutate(deckId, {
      onSuccess: (data) => {
        lastFilledRef.current = 0
        setFillBatchId(data.fill_batch_id)
        onClose()
      },
      onError: (err) => setPreflightError(extractErrorMessage(err, tr.deckManagement.fillAllErrorGeneric)),
    })
  }

  function stop() {
    if (!fillBatchId) return
    setStopping(true)
    stopFillAll.mutate(fillBatchId)
  }

  return {
    preflight,
    preflightError,
    loading: preflightMutation.isPending,
    starting: startFillAll.isPending,
    confirm,
    batchStatus: (fillBatchId && batchStatus.data) ? batchStatus.data : null,
    stopping,
    stop,
  }
}

export interface FillSelectionFlow {
  /** Whether the cost-disclosure sheet is up. */
  sheetOpen: boolean
  open: () => void
  cancel: () => void
  confirm: () => void
  preflight: FillAllPreflightResponse | null
  preflightError: string | null
  loading: boolean
  /** Resolving the selection server-side, before the first card is filled. */
  resolving: boolean
  /** `done / total` while the loop runs. */
  progress: { done: number; total: number } | null
  /** A refusal — set when the selection could not be resolved at all. */
  error: string | null
}

/**
 * The multiselect bar's `Fill`: disclose the cost over the SELECTION, then —
 * on confirm — resolve that selection server-side and loop the per-card Fill
 * endpoint over the ids it names.
 *
 * The loop's list comes from the server, never from `selection` itself: a
 * whole-deck selection holds no ids at all, so filling "what the client has"
 * would quote and do less work than the reader asked for (brief §23 item 9).
 * A failed resolve refuses outright rather than filling the loaded subset.
 */
export function useFillSelectionFlow(
  deckId: string,
  selection: DeckSelection,
  setSelection: Dispatch<SetStateAction<DeckSelection>>,
): FillSelectionFlow {
  const { tr } = useLocale()
  const fillCard = useFillCard()
  const resolveSelection = useResolveSelection()
  const preflightMutation = useFillSelectionPreflight()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [preflight, setPreflight] = useState<FillAllPreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [resolving, setResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetched fresh every time the sheet opens — never on the button tap that
  // preceded it, and never a cache hit: the split can move between opens the
  // same way Fill-all's own preflight can. `allow_ai=False` server-side, so
  // opening this sheet — even cancelling it — spends no AI quota.
  useEffect(() => {
    if (!sheetOpen) return
    setPreflight(null)
    setPreflightError(null)
    preflightMutation.mutate(
      { deckId, selection: toSelectionRequest(selection) },
      {
        onSuccess: (data) => setPreflight(data),
        onError: (err) => setPreflightError(
          extractErrorMessage(err, tr.deckManagement.fillSelectionErrorGeneric),
        ),
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen])

  async function confirm() {
    setSheetOpen(false)
    setError(null)
    setResolving(true)
    let ids: string[]
    try {
      const resolved = await resolveSelection.mutateAsync({
        deckId, selection: toSelectionRequest(selection), includeIds: true,
      })
      ids = resolved.card_ids ?? []
    } catch {
      // Refuse rather than fill the subset this client happens to hold.
      setResolving(false)
      setError(tr.deckManagement.fillCouldNotResolve)
      return
    }
    setResolving(false)
    if (ids.length === 0) return

    setProgress({ done: 0, total: ids.length })
    for (let i = 0; i < ids.length; i++) {
      try {
        await fillCard.mutateAsync(ids[i])
      } catch {
        // A single card's Fill failing (rate limit, network) doesn't abort the
        // rest of the selection — same "never lose the batch to one failure"
        // spirit as the deck-wide Fill-all.
      }
      setProgress({ done: i + 1, total: ids.length })
    }
    setProgress(null)
    setSelection(EMPTY_SELECTION)
  }

  return {
    sheetOpen,
    open: () => setSheetOpen(true),
    cancel: () => setSheetOpen(false),
    confirm,
    preflight,
    preflightError,
    loading: preflightMutation.isPending,
    resolving,
    progress,
    error,
  }
}
